import React from 'react'
import { useT } from '../../lib/i18n'
import { useApp } from '../../lib/context'
import { useAggregate } from '../../lib/api'
import Sparkline from '../Sparkline'
import { normalizePoints, toSmoothPath, toSmoothAreaPath } from '../../lib/charts'
import type { ChartType } from '../../lib/types'

const CHART_W = 560
const CHART_H = 120
const PAD = 24

const SERIES = [
  { key: 'actions',   color: 'var(--chart-actions)', label: 'chart_actions' },
  { key: 'completed', color: 'var(--health-good)',   label: 'chart_done'    },
  { key: 'errors',    color: 'var(--health-crit)',   label: 'chart_errors'  },
]

function MainChart({ buckets, chartType }: { buckets: { totals: Record<string, number> }[]; chartType: ChartType }) {
  if (buckets.length === 0) return null

  return (
    <svg width="100%" viewBox={`0 0 ${CHART_W} ${CHART_H + PAD}`} style={{ display: 'block' }}>
      {SERIES.map(({ key, color }) => {
        const data = buckets.map((b) => b.totals[key] ?? 0)
        const pts = normalizePoints(data, CHART_W - PAD, CHART_H, 4)

        if (chartType === 'bar') {
          const barW = Math.max(2, (CHART_W - PAD) / (data.length * 2))
          const max = Math.max(...data, 1)
          return (
            <g key={key}>
              {data.map((v, i) => {
                const bh = (v / max) * CHART_H
                const x = PAD / 2 + i * ((CHART_W - PAD) / data.length)
                return (
                  <rect
                    key={i}
                    x={x}
                    y={CHART_H - bh}
                    width={barW}
                    height={bh}
                    fill={color}
                    opacity={0.7}
                  />
                )
              })}
            </g>
          )
        }

        const linePath = toSmoothPath(pts)
        const areaPath = toSmoothAreaPath(pts, CHART_H)

        return (
          <g key={key}>
            {chartType === 'area' && (
              <path d={areaPath} fill={color} fillOpacity={0.1} stroke="none" />
            )}
            <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
          </g>
        )
      })}

      {/* Axe X */}
      <line x1={PAD / 2} y1={CHART_H} x2={CHART_W - PAD / 2} y2={CHART_H} stroke="var(--border)" strokeWidth={1} />
    </svg>
  )
}

export default function Evolution() {
  const t = useT()
  const { period, chartType, setChartType } = useApp()
  const { data, loading } = useAggregate(period)

  const buckets = data?.buckets ?? []
  const allBuckets = buckets.length > 0 ? buckets : []

  if (loading && !data) return (
    <div style={{ padding: '24px', color: 'var(--text-muted)' }}>{t('loading')}</div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['line', 'area', 'bar'] as ChartType[]).map((ct) => (
            <button
              key={ct}
              onClick={() => setChartType(ct)}
              style={{
                padding: '4px 10px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: chartType === ct ? 'var(--accent-dim)' : 'transparent',
                color: chartType === ct ? 'var(--accent)' : 'var(--text-muted)',
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {t(`chart_${ct}`)}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          {SERIES.map(({ key, color, label }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
              <span style={{ width: '10px', height: '3px', background: color, display: 'inline-block', borderRadius: '2px' }} />
              <span style={{ color: 'var(--text-muted)' }}>{t(label)}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 8px' }}>
        {allBuckets.length > 0 ? (
          <MainChart buckets={allBuckets} chartType={chartType} />
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
            {t('no_data')}
          </div>
        )}
      </div>

      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>
        {t('projects')} — mini-charts
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px' }}>
        {buckets.map((b) => {
          const actions = [b.totals.actions]
          return (
            <div key={b.projectId} style={{
              padding: '10px 12px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {b.projectId}
              </div>
              <Sparkline
                data={actions}
                color={b.health === 'critical' ? 'var(--health-crit)' : b.health === 'warning' ? 'var(--health-warn)' : 'var(--accent)'}
                type="area"
                width={130}
                height={28}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                <span>{b.totals.actions} act.</span>
                <span style={{ color: b.trend >= 0 ? 'var(--health-good)' : 'var(--health-crit)' }}>
                  {b.trend >= 0 ? '+' : ''}{b.trend}%
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
